import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { insertLenderSchema, type Lender } from "@shared/schema";
import { z } from "zod";
import { useLocation } from "wouter";

type LenderFormData = z.infer<typeof insertLenderSchema>;

interface LenderFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lender?: Lender;
  onLenderCreated?: (lender: Lender) => void;
}

export function LenderFormModal({
  open,
  onOpenChange,
  lender,
  onLenderCreated,
}: LenderFormModalProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const isEdit = !!lender;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dangerZoneOpen, setDangerZoneOpen] = useState(false);

  const defaultValues: Partial<LenderFormData> = {
    lenderName: "",
    lenderCode: "",
    lenderType: "bank",
    status: "active",
    bdmName: "",
    bdmEmail: "",
    bdmPhone: "",
    bdmMobile: "",
    accreditationStatus: "pending",
    aggregatorId: "",
    preferredSecurityTypes: [],
    excludedIndustries: [],
    defaultUpfrontRate: "0.0000",
    defaultTrailRate: "0.0000",
    clawbackPeriodMonths: 24,
    applicationPortalUrl: "",
    apiIntegrationEnabled: false,
    notes: "",
  };

  const form = useForm<LenderFormData>({
    resolver: zodResolver(insertLenderSchema),
    defaultValues,
    mode: "onChange",
    criteriaMode: "all",
  });

  // Normalize database record for form (omit fields not in insert schema)
  // This is critical for edit mode - the database returns extra fields like
  // id, createdAt, updatedAt, createdBy, updatedBy, organisationId that
  // would cause Zod validation to fail silently if included in form.reset()
  const normalizeForForm = (record: Lender): Partial<LenderFormData> => {
    // Only include fields that are in the insertLenderSchema
    return {
      lenderName: record.lenderName,
      lenderCode: record.lenderCode,
      lenderType: record.lenderType,
      status: record.status || "active",
      bdmName: record.bdmName || "",
      bdmEmail: record.bdmEmail || "",
      bdmPhone: record.bdmPhone || "",
      bdmMobile: record.bdmMobile || "",
      accreditationStatus: record.accreditationStatus || "pending",
      aggregatorId: record.aggregatorId || "",
      accreditationDate: record.accreditationDate || undefined,
      accreditationExpiry: record.accreditationExpiry || undefined,
      preferredSecurityTypes: record.preferredSecurityTypes || [],
      excludedIndustries: record.excludedIndustries || [],
      defaultUpfrontRate: record.defaultUpfrontRate || "0.0000",
      defaultTrailRate: record.defaultTrailRate || "0.0000",
      clawbackPeriodMonths: record.clawbackPeriodMonths || 24,
      applicationPortalUrl: record.applicationPortalUrl || "",
      apiIntegrationEnabled: record.apiIntegrationEnabled || false,
      notes: record.notes || "",
    };
  };

  useEffect(() => {
    if (open) {
      if (lender) {
        // Normalize lender data before resetting form to avoid Zod validation issues
        const normalizedData = normalizeForForm(lender);
        form.reset(normalizedData);
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, lender, form]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset(defaultValues);
    }
    onOpenChange(newOpen);
  };

  const createMutation = useMutation({
    mutationFn: async (data: LenderFormData) => {
      const response = await apiRequest("POST", "/api/crm/lenders", data);
      return response.json();
    },
    onSuccess: async (newLender: Lender) => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/lenders"] });
      toast({
        title: "Lender created",
        description: "The lender has been created successfully.",
      });
      onOpenChange(false);
      form.reset(defaultValues);

      if (onLenderCreated) {
        onLenderCreated(newLender);
      }
    },
    onError: (error: Error) => {
      const isCodeDuplicate = error.message.includes("lender_code") ||
                              error.message.includes("duplicate key") ||
                              error.message.includes("unique constraint");

      toast({
        title: "Error",
        description: isCodeDuplicate
          ? "A lender with this code already exists. Please use a different code."
          : error.message,
        variant: "destructive",
      });

      if (isCodeDuplicate) {
        form.setError("lenderCode", {
          type: "manual",
          message: "This lender code is already in use",
        });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: LenderFormData) => {
      const response = await apiRequest("PATCH", `/api/crm/lenders/${lender?.id}`, data);
      return response.json();
    },
    onSuccess: (updatedLender: Lender) => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/lenders"] });
      queryClient.invalidateQueries({ queryKey: [`/api/crm/lenders/${lender?.id}`] });
      toast({
        title: "Lender updated",
        description: "The lender has been updated successfully.",
      });
      onOpenChange(false);

      if (onLenderCreated) {
        onLenderCreated(updatedLender);
      }
    },
    onError: (error: Error) => {
      const isCodeDuplicate = error.message.includes("lender_code") ||
                              error.message.includes("duplicate key") ||
                              error.message.includes("unique constraint");

      toast({
        title: "Error",
        description: isCodeDuplicate
          ? "A lender with this code already exists. Please use a different code."
          : error.message,
        variant: "destructive",
      });

      if (isCodeDuplicate) {
        form.setError("lenderCode", {
          type: "manual",
          message: "This lender code is already in use",
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/crm/lenders/${lender?.id}`);

      // ISSUE-063: Handle 409 Conflict when lender has active products
      if (response.status === 409) {
        const errorData = await response.json();
        const error = new Error(errorData.message || "Cannot delete lender with active products") as Error & { code?: string; activeProductCount?: number };
        error.code = errorData.code;
        error.activeProductCount = errorData.activeProductCount;
        throw error;
      }

      if (!response.ok) {
        throw new Error("Failed to delete lender");
      }
      // 204 No Content - don't try to parse JSON from empty response
      if (response.status === 204) {
        return null;
      }
      return response.json();
    },
    onSuccess: () => {
      // Step 1: Invalidate the specific lender detail query to prevent refetch errors
      queryClient.removeQueries({ queryKey: [`/api/crm/lenders/${lender?.id}`] });

      // Step 2: Show success toast
      toast({
        title: "Lender deleted",
        description: "The lender has been deleted successfully.",
      });

      // Step 3: Close modal
      onOpenChange(false);

      // Step 4: Navigate away (after modal closes)
      // Use setTimeout to allow React to process modal closure before navigation
      setTimeout(() => {
        navigate("/lenders");
        // Step 5: Refetch list after navigation (not before)
        queryClient.refetchQueries({ queryKey: ["/api/crm/lenders"] });
      }, 100);
    },
    onError: () => {
      // ISSUE-063 Rework #3: Always show user-friendly message with red toast
      // The only reason deletion fails is active products (backend enforces this)
      toast({
        title: "Cannot Delete Lender",
        description: "This lender has active products. Please delete or deactivate all products before deleting this lender.",
        variant: "destructive",
      });
      // Close the delete dialog to prevent re-attempts
      setDeleteDialogOpen(false);
    },
  });

  const onSubmit = (data: LenderFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const _handleDelete = () => {
    deleteMutation.mutate();
  };

  const isPending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Lender" : "Add Lender"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update lender information"
              : "Create a new lender record"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="lenderName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lender Name *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Commonwealth Bank"
                          data-testid="input-lender-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lenderCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lender Code *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="CBA"
                          data-testid="input-lender-code"
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Unique identifier (e.g., CBA, ANZ, WBC)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lenderType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lender Type *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-lender-type">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="bank">Bank</SelectItem>
                          <SelectItem value="non_bank">Non-Bank</SelectItem>
                          <SelectItem value="private">Private</SelectItem>
                          <SelectItem value="smsf_lender">SMSF Lender</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* BDM Contact Details */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">BDM Contact Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="bdmName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>BDM Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          placeholder="John Smith"
                          data-testid="input-bdm-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bdmEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>BDM Email</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="email"
                          placeholder="bdm@lender.com.au"
                          data-testid="input-bdm-email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bdmPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>BDM Phone</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="tel"
                          placeholder="(02) 1234 5678"
                          data-testid="input-bdm-phone"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bdmMobile"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>BDM Mobile</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="tel"
                          placeholder="0412 345 678"
                          data-testid="input-bdm-mobile"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Accreditation */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Accreditation</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="accreditationStatus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Accreditation Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-accreditation-status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="expired">Expired</SelectItem>
                          <SelectItem value="renewal_due">Renewal Due</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="aggregatorId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Aggregator ID</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          placeholder="AGG123456"
                          data-testid="input-aggregator-id"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="accreditationDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Accreditation Date</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="date"
                          data-testid="input-accreditation-date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="accreditationExpiry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Accreditation Expiry</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="date"
                          data-testid="input-accreditation-expiry"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Commission Defaults */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Commission Defaults</h3>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="defaultUpfrontRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default Upfront Rate</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="number"
                          step="0.0001"
                          placeholder="0.0200"
                          data-testid="input-default-upfront-rate"
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        As decimal (e.g., 0.0200 for 2%)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="defaultTrailRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default Trail Rate</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="number"
                          step="0.0001"
                          placeholder="0.0000"
                          data-testid="input-default-trail-rate"
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        As decimal (e.g., 0.0010 for 0.1%)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="clawbackPeriodMonths"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Clawback Period (months)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="number"
                          placeholder="24"
                          data-testid="input-clawback-period"
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === '' ? undefined : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Integration */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Integration</h3>
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="applicationPortalUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Application Portal URL</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="url"
                          placeholder="https://apply.lender.com.au"
                          data-testid="input-portal-url"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="apiIntegrationEnabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>API Integration Enabled</FormLabel>
                        <FormDescription className="text-xs">
                          Enable automated application submission and status updates
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                          data-testid="switch-api-integration"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          value={field.value || ""}
                          placeholder="Additional notes about this lender..."
                          data-testid="input-notes"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Danger Zone - Only shown in edit mode */}
            {isEdit && (
              <div className="border-t pt-6 mt-6">
                <Collapsible open={dangerZoneOpen} onOpenChange={setDangerZoneOpen}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-red-600 dark:text-red-400">
                        Danger Zone
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        Irreversible and destructive actions
                      </p>
                    </div>
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                      >
                        {dangerZoneOpen ? "Hide" : "Show"}
                      </Button>
                    </CollapsibleTrigger>
                  </div>

                  <CollapsibleContent className="mt-4">
                    <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-900/20 p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-red-900 dark:text-red-100">
                            Delete this lender
                          </h4>
                          <p className="text-xs text-red-800 dark:text-red-200 mt-1">
                            This action is blocked if the lender has active products. To delete:
                          </p>
                          <ul className="text-xs text-red-800 dark:text-red-200 mt-2 ml-4 list-disc space-y-1">
                            <li>First deactivate or delete all products</li>
                            <li>Then delete the lender</li>
                          </ul>
                          <p className="text-xs text-red-800 dark:text-red-200 mt-2">
                            Deletion will permanently remove all fee groups, fees, commission structures, and pricing tiers.
                          </p>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="mt-4"
                            onClick={() => setDeleteDialogOpen(true)}
                            disabled={deleteMutation.isPending}
                            data-testid="button-delete-lender-modal"
                          >
                            <Trash2 className="mr-2 h-3 w-3" />
                            Delete Lender
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}

            <DialogFooter className="gap-2 mt-6">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isPending}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isPending}
                  data-testid="button-save"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEdit ? "Update" : "Create"} Lender
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lender</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{lender?.lenderName}"? This action cannot be undone
              and will permanently remove all associated fee groups, fees, commissions, and pricing tiers.
              Note: Deletion will be blocked if there are active products.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDeleteDialogOpen(false);
                deleteMutation.mutate();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
